import React, { useState } from 'react'
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons'
import { faStar as faStarLight } from '@fortawesome/pro-light-svg-icons'
import { FaIcon } from '../../App/Components/FaIcon/FaIcon'
import '../Chat.css'

type ChatRatingTexts = {
  prompt: string
  commentPlaceholder: string
  submitButton: string
  thankYou: string
}

type ChatRatingProps = {
  onSubmit: (rating: number, comment?: string) => Promise<void>
  texts: ChatRatingTexts
  submitted: boolean
}

export const ChatRating: React.FC<ChatRatingProps> = ({
  onSubmit,
  texts,
  submitted,
}) => {
  const [selectedRating, setSelectedRating] = useState(0)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (selectedRating === 0 || isSubmitting) return
    setIsSubmitting(true)
    try {
      await onSubmit(selectedRating, comment.trim().slice(0, 1000) || undefined)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="chat__rating" data-testid="chat-rating-thanks">
        <p className="chat__rating-thanks">{texts.thankYou}</p>
      </div>
    )
  }

  return (
    <div className="chat__rating" data-testid="chat-rating">
      <p className="chat__rating-prompt">{texts.prompt}</p>
      <div
        className="chat__rating-stars"
        onMouseLeave={() => setHoveredRating(0)}
      >
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= (hoveredRating || selectedRating)
          return (
            <button
              key={star}
              type="button"
              className={`chat__rating-star ${isFilled ? 'chat__rating-star--active' : ''}`}
              onClick={() => setSelectedRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              data-testid={`chat-rating-star-${star}`}
              aria-label={`${star} star${star > 1 ? 's' : ''}`}
            >
              <FaIcon
                icon={isFilled ? faStarSolid : faStarLight}
                cssStyle={{ fontSize: 28 }}
              />
            </button>
          )
        })}
      </div>
      {selectedRating > 0 && (
        <>
          <textarea
            className="chat__rating-comment"
            placeholder={texts.commentPlaceholder}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            rows={2}
            data-testid="chat-rating-comment"
          />
          <button
            type="button"
            className={`chat__rating-submit ${isSubmitting ? 'chat__rating-submit--disabled' : ''}`}
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-testid="chat-rating-submit"
          >
            {texts.submitButton}
          </button>
        </>
      )}
    </div>
  )
}
